// IP Phone Gateway — the page's own surface (multi-instance: one card
// per row in MySQL `ipphone_gateway`). `run` dispatches one action
// against a gateway (explicit `gateway_id`, or the lowest-priority one
// that supports the action when omitted); the rest are page-level
// CRUD + connectivity probe. Mirrors the shape of the RADIUS Status
// module — same Action/Descriptor/telemetry model on the Go side.

import type { ApiClient } from "./client.js";
import type {
  IPPhoneDeleteResult,
  IPPhoneRunRequest,
  IPPhoneRunResult,
  IPPhoneSaveRequest,
  IPPhoneSaveResult,
  IPPhoneState,
  IPPhoneStateResponse,
  IPPhoneTestResult,
} from "./types.js";

export function makeIpPhoneApi(client: ApiClient) {
  function reqBody(req: IPPhoneSaveRequest): Record<string, unknown> {
    const b: Record<string, unknown> = {
      id: req.id,
      name: req.name,
      gateway_kind: req.gateway_kind,
      host: req.host,
      username: req.username,
      priority: req.priority,
    };
    if (req.password) b.password = req.password;
    if (req.extra) b.extra = req.extra;
    if (req.enabled !== undefined) b.enabled = req.enabled;
    return b;
  }

  return {
    /** The page view-model: every configured gateway with its live
     *  connection health, service-status snapshot, and per-action
     *  telemetry, plus the registry's available kinds for the Add modal.
     *  Throws if the `state` envelope is malformed. */
    async state(): Promise<IPPhoneState> {
      const r = await client.get<IPPhoneStateResponse>("/v1/api/ipphone/state");
      if (!r.ok || !r.state) throw new Error(r.error ?? "ipphone state unavailable");
      return r.state;
    },
    /** Add (`id: 0`) or edit a gateway — admin only. Empty/omitted
     *  `password` keeps the stored ciphertext (required on create). */
    save(req: IPPhoneSaveRequest) {
      return client.post<IPPhoneSaveResult>("/v1/api/ipphone/save", reqBody(req));
    },
    /** Probe a gateway's credentials without persisting. Empty password
     *  + an existing `id` falls back to the stored one. */
    test(req: IPPhoneSaveRequest) {
      return client.post<IPPhoneTestResult>("/v1/api/ipphone/test", reqBody(req));
    },
    /** Dispatch one action against a gateway. Every gateway-touching
     *  action goes through here. */
    run(req: IPPhoneRunRequest) {
      const b: Record<string, unknown> = { action: req.action, args: req.args ?? {} };
      if (req.gateway_id) b.gateway_id = req.gateway_id;
      return client.post<IPPhoneRunResult>("/v1/api/ipphone/run", b);
    },
    /** Delete a gateway — admin only. */
    del(id: number) {
      return client.post<IPPhoneDeleteResult>("/v1/api/ipphone/delete", { id });
    },
    /** Wipe the rolling telemetry for one (gateway, action) pair — admin
     *  only. Action key `"login"` resets the gateway's Connection card. */
    clearHistory(gatewayId: number, action: string) {
      return client.post<IPPhoneSaveResult>("/v1/api/ipphone/clear_history", {
        gateway_id: gatewayId,
        action,
      });
    },
  };
}

export type IpPhoneApi = ReturnType<typeof makeIpPhoneApi>;
